import { observabilityAppService } from '@ctct/myaccount-utils-static';

import type { ApplePayBillingAddress, ApplePayShippingAddress, ApplePayTokenData } from './types';
import { distUI } from '../DistUI';

export const SECURE_PAY_EVENTS = {
  SECUREPAY_ID: 'iframe_cc_securepay',
  APPLE_PAY_SECUREPAY_ID: 'iframe_applepay_securepay',
  CSS_ADDED_EVENT: 'CSS added',
  GOT_TOKEN_FAILURE_EVENT: 'got-token-failure-event',
  GOT_TOKEN_SUCCESS_EVENT: 'got-token-success-event',
  INFO_SECURECODE_MOUSEOVER_EVENT: 'info-securecode',
  INFO_SECURECODE_MOUSEOUT_EVENT: 'info-securecode-close',
  INFO_BANK_INFO_MOUSEOVER_EVENT: 'info-bank-information',
  INFO_BANK_INFO_MOUSEOUT_EVENT: 'info-bank-information-close',
  APPLE_PAY_TOKEN_EVENT: 'securepay-token',
  APPLE_PAY_ADDRESS_EVENT: 'apple-pay-address',
  APPLE_PAY_TAX_REQUEST_EVENT: 'apple-pay-get-tax-value',
  APPLE_PAY_OPERATION_ABORTED: 'apple-pay-operation-aborted',
  VALIDATION_SUCCESS_EVENT_KEY: 'myact-react-static.token.success',
  VALIDATION_FAILURE_EVENT_KEY: 'myact-react-static.token.failure',
};

// 001001 appears to be the Constant Contact api key
export const SECURE_PAY_CLIENT_ID = '001001';

export interface ApplePayPaymentData {
  amount: number | string;
  taxAmount?: number | string;
  currency: string;
  label: string;
}

export interface ApplePayCreditCardResponse {
  data: {
    clientId: string;
    id: string;
    creditCard: {
      data: string;
      cardType: string;
      cardNumber: string;
      cardExpiration: string;
    };
    binData: {
      number: string;
      brand: string;
      bank: string;
      country: string;
      countryCode: string;
      type: string;
      level: string | null;
    };
  };
}

export class SecurePay {
  private callback: (...args: any[]) => void;

  constructor(callback: (...args: any[]) => void) {
    this.callback = callback;
  }

  handleMessage = (e) => {
    if ((e.data?.securePayMessage && e.data?.source) === 'securepay-applepay') {
      //  handle all apple pay events in this block

      const data = e.data.body;

      if (data.type === 'message' && data.payload === SECURE_PAY_EVENTS.CSS_ADDED_EVENT) {
        this.callback(SECURE_PAY_EVENTS.CSS_ADDED_EVENT);
      }

      if (data.type === SECURE_PAY_EVENTS.APPLE_PAY_TOKEN_EVENT) {
        this.callback(SECURE_PAY_EVENTS.APPLE_PAY_TOKEN_EVENT, data.payload as ApplePayTokenData);
      }

      if (data.type === SECURE_PAY_EVENTS.APPLE_PAY_ADDRESS_EVENT) {
        this.callback(SECURE_PAY_EVENTS.APPLE_PAY_ADDRESS_EVENT, data.payload as ApplePayBillingAddress);
      }

      if (data.type === SECURE_PAY_EVENTS.APPLE_PAY_TAX_REQUEST_EVENT) {
        this.callback(SECURE_PAY_EVENTS.APPLE_PAY_TAX_REQUEST_EVENT, data.payload as ApplePayShippingAddress);
      }

      if (data.type === 'event') {
        this.trackEvent(data.payload);
      }

      if (data.type === 'error' && data.payload === 'The operation was aborted.') {
        this.callback(SECURE_PAY_EVENTS.APPLE_PAY_OPERATION_ABORTED);
        this.trackEvent('ApplePay_Close');
      }

      if (data.type === 'error') {
        this.logError(data.payload);
      }

      return;
    }

    const { data: { body: messageBody = {}, securePayMessage = null } = {} } = e;

    const messageBodyConstructor = messageBody.constructor;

    if (securePayMessage !== true) {
      // This message did not come from securepay
      return;
    }

    const validMessages = [
      SECURE_PAY_EVENTS.CSS_ADDED_EVENT,
      SECURE_PAY_EVENTS.INFO_SECURECODE_MOUSEOVER_EVENT,
      SECURE_PAY_EVENTS.INFO_SECURECODE_MOUSEOUT_EVENT,
      SECURE_PAY_EVENTS.INFO_BANK_INFO_MOUSEOVER_EVENT,
      SECURE_PAY_EVENTS.INFO_BANK_INFO_MOUSEOUT_EVENT,
    ];

    if (validMessages.includes(messageBody)) {
      this.callback(messageBody);

      return;
    }

    if (messageBodyConstructor === Array) {
      const response = messageBody.map(({ message }) => message).join('. ');

      window.dispatchEvent(new CustomEvent(SECURE_PAY_EVENTS.VALIDATION_FAILURE_EVENT_KEY, { detail: { response } }));
    } else if (messageBodyConstructor === Object) {
      const response = { data: messageBody };

      window.dispatchEvent(new CustomEvent(SECURE_PAY_EVENTS.VALIDATION_SUCCESS_EVENT_KEY, { detail: { response } }));
    }
  };

  startListening = () => {
    window.addEventListener('message', this.handleMessage);
  };

  stopListening = () => {
    window.removeEventListener('message', this.handleMessage);
  };

  setApplePayPaymentData(paymentData: ApplePayPaymentData) {
    const paymentDataObject = {
      securePayMessage: true,
      body: {
        callFunction: {
          name: 'setPaymentData',
          arguments: [paymentData],
        },
      },
    };

    this.postApplePaySecurePayIframeMessage(paymentDataObject);
  }

  setApplePayTaxValue(taxValue: number | null) {
    this.postApplePaySecurePayIframeMessage({
      securePayMessage: true,
      body: {
        callFunction: {
          name: 'setTaxValue',
          // sending tax value as {} will cancel tax value request ({} is valid value)
          arguments: [taxValue !== null ? taxValue : {}],
        },
      },
    });
  }

  setShouldGetAddress(shouldGetAddress: boolean) {
    this.postApplePaySecurePayIframeMessage({
      securePayMessage: true,
      body: {
        callFunction: {
          name: 'setShouldGetAddress',
          arguments: [shouldGetAddress || ''],
        },
      },
    });
  }

  setShouldGetTaxUpdate(shouldGetTaxUpdate: boolean) {
    this.postApplePaySecurePayIframeMessage({
      securePayMessage: true,
      body: {
        callFunction: {
          name: 'setShouldGetTaxUpdate',
          arguments: [shouldGetTaxUpdate || ''],
        },
      },
    });
  }

  isApplePaySupportedInBrowser = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return Boolean((window as any).ApplePaySession?.canMakePayments());
  };

  validatePayment = () => {
    this.postSecurePayIframeMessage({
      securePayMessage: true,
      body: {
        callFunction: {
          name: 'checkErrorAndGetToken',
          arguments: [],
        },
      },
    });

    return new Promise((resolve, reject) => {
      let removeEventListeners;
      const onSuccess = (e) => {
        removeEventListeners();
        this.callback(SECURE_PAY_EVENTS.GOT_TOKEN_SUCCESS_EVENT, e.detail.response);
        resolve(e.detail.response);
      };
      const onFailure = (e) => {
        removeEventListeners();
        this.callback(SECURE_PAY_EVENTS.GOT_TOKEN_FAILURE_EVENT, e.detail.response);
        reject(new Error(e.detail.response));
      };

      removeEventListeners = () => {
        window.removeEventListener(SECURE_PAY_EVENTS.VALIDATION_SUCCESS_EVENT_KEY, onSuccess);
        window.removeEventListener(SECURE_PAY_EVENTS.VALIDATION_FAILURE_EVENT_KEY, onFailure);
      };
      window.addEventListener(SECURE_PAY_EVENTS.VALIDATION_SUCCESS_EVENT_KEY, onSuccess);
      window.addEventListener(SECURE_PAY_EVENTS.VALIDATION_FAILURE_EVENT_KEY, onFailure);
    });
  };

  injectCss(e, filename: string) {
    const {
      target: { contentWindow },
    } = e;

    const customCSS = {
      securePayMessage: true,
      body: {
        callFunction: {
          name: 'useSelfHostedStyles',
          arguments: [filename],
        },
      },
    };

    contentWindow.postMessage(JSON.stringify(customCSS), '*');
  }

  private getTokenContentWindow(id: string): Window | null {
    const element = document.getElementById(id);

    if (!element) {
      this.logError('SecurePay element not found');

      return null;
    }

    if (!(element instanceof HTMLIFrameElement)) {
      this.logError('SecurePay element is not an iframe');

      return null;
    }

    const { contentWindow } = element;

    if (!contentWindow) {
      this.logError('SecurePay iframe is not initialized');

      return null;
    }

    return contentWindow;
  }

  private trackEvent(action: string): void {
    distUI.track({ action });
  }

  private logError(message: string): void {
    observabilityAppService.notify(new Error(message));
  }

  private postSecurePayIframeMessage(
    message: { securePayMessage: boolean; body: Record<string, unknown> },
    // TODO: set the origin to a known value instead of wildcard
    origin = '*'
  ) {
    const tokenContentWindow = this.getTokenContentWindow(SECURE_PAY_EVENTS.SECUREPAY_ID);

    tokenContentWindow?.postMessage(JSON.stringify(message), origin);
  }

  private postApplePaySecurePayIframeMessage(
    message: { securePayMessage: boolean; body: Record<string, unknown> },
    // TODO: set the origin to a known value instead of wildcard
    origin = '*'
  ) {
    const tokenContentWindow = this.getTokenContentWindow(SECURE_PAY_EVENTS.APPLE_PAY_SECUREPAY_ID);

    tokenContentWindow?.postMessage(JSON.stringify(message), origin);
  }
}
